import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function AdminEvents() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ 
    title: '', 
    description: '', 
    location: '', 
    date: '', 
    image: null 
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); 

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Accès non autorisé');

      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      setEvents(data);
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEvents = events.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(events.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
    const filePath = `events/${fileName}`;
    const { error } = await supabase.storage.from('event-images').upload(filePath, file);
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('event-images').getPublicUrl(filePath);
    return publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) throw new Error('Vous devez être connecté pour gérer les événements');

      let imageUrl = editingEvent ? editingEvent.image_url : '';
      if (form.image) {
        imageUrl = await uploadImage(form.image);
      }

      if (editingEvent) {
        const { error } = await supabase
          .from('events')
          .update({
            title: form.title,
            description: form.description,
            location: form.location,
            date: form.date,
            image_url: imageUrl,
            user_id: user.id
          })
          .eq('id', editingEvent.id);

        if (error) throw error;
        setSuccess('Événement modifié avec succès');
      } else {
        const { error } = await supabase
          .from('events')
          .insert([{
            title: form.title,
            description: form.description,
            location: form.location,
            date: form.date,
            image_url: imageUrl,
            user_id: user.id
          }]);

        if (error) throw error;
        setSuccess('Événement ajouté avec succès');
      }

      resetForm();
      fetchEvents();
      setCurrentPage(1);
    } catch (error) {
      console.error('Erreur:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditingEvent(null);
    setForm({ title: '', description: '', location: '', date: '', image: null });
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title,
      description: event.description,
      location: event.location,
      date: event.date,
      image: null,
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Confirmer la suppression ?')) {
      try {
        const { error } = await supabase.from('events').delete().eq('id', id);
        if (error) throw error;
        fetchEvents();
        setSuccess('Événement supprimé avec succès');
      } catch (error) {
        console.error('Erreur:', error);
        setError(error.message);
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="page-header">
        <h3 className="page-title">Gestion des événements</h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item" style={{color: "#0063cc"}}>Tables</li>
            <li className="breadcrumb-item active" aria-current="page">Événements</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{editingEvent ? 'Modifier' : 'Ajouter'} un événement</h4>
              
              <form className="forms-sample" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Titre</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    placeholder="Titre de l'événement"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    rows="4"
                    placeholder="Description"
                    value={form.description}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Lieu</label>
                  <input
                    type="text"
                    className="form-control"
                    name="location"
                    placeholder="Lieu"
                    value={form.location}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Date et heure</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
  <label>Image (max 2MB)</label>
  <div className="file-input-container">
    <input
      type="file"
      className="file-input"
      id="eventImage"
      name="image"
      accept="image/*"
      onChange={handleChange}
    />
    <label htmlFor="eventImage" className="file-input-label">
      <span className="file-input-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
      </span>
      <span className="file-input-text">
        {form.image ? form.image.name : "Choisir un fichier"}
      </span>
    </label>
    {form.image && (
      <button 
        type="button" 
        className="file-remove-btn"
        onClick={() => setForm(prev => ({ ...prev, image: null }))}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    )}
  </div>
</div>
                <button 
                  type="submit" 
                  className="btn btn-primary mr-2" 
                  disabled={uploading}
                >
                  {uploading ? "En cours..." : (editingEvent ? "Modifier" : "Ajouter")}
                </button>
                
                {editingEvent && (
                  <button 
                    type="button" 
                    className="btn btn-dark"
                    onClick={resetForm}
                  >
                    Annuler
                  </button>
                )}

                {error && (
                  <div className="alert alert-danger mt-3">
                    ❌ {error}
                  </div>
                )}
                
                {success && (
                  <div className="alert alert-success mt-3">
                    ✅ {success}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Liste des événements</h4>
              <div className="table-responsive custom-scroll">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Titre</th>
                      <th>Description</th>
                      <th>Lieu</th>
                      <th>Date</th>
                      <th>Image</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentEvents.map(event => (
                      <tr key={event.id}>
                        <td>{event.title}</td>
                        <td>{event.description}</td>
                        <td>{event.location}</td>
                        <td>{new Date(event.date).toLocaleString()}</td>
                        <td>
                          {event.image_url && (
                            <img 
                              src={event.image_url} 
                              alt="Event" 
                              style={{ maxWidth: '100px' }} 
                            />
                          )}
                        </td>
                        <td>
                          <button 
                            onClick={() => {handleEdit(event); scrollToTop();}}
                            className="btn btn-sm btn-primary mr-2"
                          >
                            Modifier
                          </button>
                          <button 
                            onClick={() => handleDelete(event.id)}
                            className="btn btn-sm btn-danger"
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {events.length > 0 && (
                <nav aria-label="Page navigation" className="mt-4">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={goToPreviousPage} aria-label="Previous">
                        <span aria-hidden="true">&laquo;</span>
                      </button>
                    </li>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => paginate(page)}>
                          {page}
                        </button>
                      </li>
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={goToNextPage} aria-label="Next">
                        <span aria-hidden="true">&raquo;</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminEvents;